import { Button, Select, TextInput, addToast } from '@octano/global-ui';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { Card, Col, Row } from 'reactstrap';

import GoBackButton from '../../../../components/buttons/GoBackButton';
import { useStepState } from '../../../../components/step/useStepState';
import { SectionTitle } from '../../../../components/text';
import { PathsLayouts } from '../../../../config/routes';
import { useValidations } from '../../../../hooks/useValidations';
import { TranslationsKeys } from '../../../../locales/translations';
import { ModalityCodesEnum } from '../../../SectionsMaintainer/enums/modality-codes.enum';
import { useAcademicOfferWizard } from '../context/AcademicOfferWizardContext';
import { createAcademicOffer, updateAcademicOfferCurrentStep } from '../api';
import { AcademicOfferInitialValues } from '../hooks/AcademicOfferDataLoader';
import { useAcademicOfferFormData } from '../hooks/AcademicOfferFormDataLoader';
import {
  CeAcademicOfferStepEnum,
  ContinuousEducationType,
  CreateCeAcademicOfferPayload,
  StudyPlan,
} from '../types';

export interface AcademicOfferFormValues {
  programType: { label: string; value: ContinuousEducationType } | null;
  studyPlan: {
    label: string;
    value: number;
    data?: StudyPlan;
  } | null;
  campus: { label: string; value: number } | null;
  quota: string;
  completionDaysLimit: string;
}

interface Props {
  initialValues?: AcademicOfferInitialValues;
}

export function AcademicOfferCreateForm({ initialValues }: Props) {
  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_ACADEMIC_OFFERS,
  );
  const history = useHistory();
  const location = useLocation();
  const { msgValidations, validateTextNotEmpty } = useValidations();
  const { data: formData } = useAcademicOfferFormData();
  const { setCeAcademicOfferId } = useAcademicOfferWizard();
  const { nextStep } = useStepState();
  const isLoadingInitialValues = useRef(false);

  const searchParams = new URLSearchParams(location.search);
  const urlProgramType = searchParams.get(
    'programType',
  ) as ContinuousEducationType | null;

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { isSubmitting },
  } = useForm<AcademicOfferFormValues>({
    mode: 'all',
    defaultValues: initialValues,
  });

  const programType = useWatch({ control, name: 'programType' });
  const selectedStudyPlan = useWatch({ control, name: 'studyPlan' });

  // Actualizar el form cuando cambien los initialValues
  useEffect(() => {
    if (initialValues) {
      isLoadingInitialValues.current = true;
      reset(initialValues);
      setTimeout(() => {
        isLoadingInitialValues.current = false;
      }, 100);
    }
  }, [initialValues, reset]);

  // Opciones del selector de tipo de programa
  const programTypeOptions = useMemo(
    () => [
      {
        label: t('form.programType.course'),
        value: ContinuousEducationType.COURSE,
      },
      {
        label: t('form.programType.program'),
        value: ContinuousEducationType.PROGRAM_COURSE,
      },
    ],
    [t],
  );

  const defaultProgramType = useMemo(() => {
    if (!urlProgramType) {
      return null;
    }
    return programTypeOptions.find((o) => o.value === urlProgramType) ?? null;
  }, [urlProgramType, programTypeOptions]);

  useEffect(() => {
    // Solo aplicar el programType de la URL si no hay initialValues (nueva oferta)
    if (defaultProgramType && !initialValues) {
      setValue('programType', defaultProgramType);
    }
  }, [defaultProgramType, initialValues, setValue]);

  // Filtrar StudyPlans por tipo de programa seleccionado
  const studyPlanOptions = useMemo(() => {
    if (!formData?.studyPlans || !programType?.value) {
      return [];
    }

    return formData.studyPlans
      .filter((sp) => sp.studyPlanCe?.type === programType.value)
      .map((sp) => ({
        label: `${sp.code} - ${sp.name}`,
        value: sp.id,
        data: sp,
      }));
  }, [formData, programType]);

  // Opciones de campus
  const campusOptions = useMemo(() => {
    return (
      formData?.campuses.map((c) => ({ label: c.name, value: c.id })) ?? []
    );
  }, [formData]);

  // Datos del plan seleccionado
  const selectedPlanData = useMemo(() => {
    return selectedStudyPlan?.data ?? null;
  }, [selectedStudyPlan]);

  const isOnlineAutoAttended = useMemo(() => {
    return selectedPlanData?.modality?.deliveryMode === ModalityCodesEnum.OA;
  }, [selectedPlanData]);

  // Limpiar campos cuando cambia el tipo de programa (solo si el usuario lo cambió manualmente)
  useEffect(() => {
    // No limpiar si estamos cargando initialValues
    if (!isLoadingInitialValues.current) {
      setValue('studyPlan', null);
      setValue('campus', null);
    }
  }, [programType, setValue]);

  const onSubmit = useCallback(
    async (values: AcademicOfferFormValues) => {
      if (!values.programType || !values.studyPlan || !values.campus) return;

      const payload: CreateCeAcademicOfferPayload = {
        studyPlanId: values.studyPlan.value,
        campusId: values.campus.value,
        quota: values.quota ? parseInt(values.quota, 10) : undefined,
      };

      if (isOnlineAutoAttended && values.completionDaysLimit) {
        payload.completionDaysLimit = parseInt(values.completionDaysLimit, 10);
      }

      const response = await createAcademicOffer(payload);

      if (!response.error && response.data) {
        const offerId = response.data.id;
        setCeAcademicOfferId(offerId);

        await updateAcademicOfferCurrentStep(
          offerId,
          CeAcademicOfferStepEnum.Sections,
        );

        addToast({
          icon: 'success',
          color: 'success',
          text: t('messages.createSuccess'),
        });

        history.replace(
          `${PathsLayouts.continuousEducation}/academic-offers/${offerId}/wizard`,
        );
        nextStep();
      } else {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t('messages.createError'),
        });
      }
    },
    [history, t, isOnlineAutoAttended, setCeAcademicOfferId, nextStep],
  );

  return (
    <Card className="p-4 mt-2 mx-3">
      <div className="mb-4">
        <GoBackButton
          onClick={() =>
            history.push(`${PathsLayouts.continuousEducation}/academic-offers`)
          }
        />
      </div>
      <SectionTitle text={t('create.title')} />

      <form onSubmit={handleSubmit(onSubmit)}>
        <Row className="mb-4">
          {/* Tipo de programa */}
          <Col xs={12} md={6} lg={3} className="mb-3">
            <Select
              name="programType"
              label={t('form.programType.label')}
              options={programTypeOptions}
              control={control}
              rules={{ validate: validateTextNotEmpty }}
              disabled={!!urlProgramType}
            />
          </Col>

          {/* Selector de StudyPlan */}
          <Col xs={12} md={6} lg={3} className="mb-3">
            <Select
              name="studyPlan"
              label={t('form.studyPlan.label')}
              options={studyPlanOptions}
              control={control}
              rules={{ validate: validateTextNotEmpty }}
              disabled={!programType}
            />
          </Col>

          {/* Selector de Campus */}
          <Col xs={12} md={6} lg={3} className="mb-3">
            <Select
              name="campus"
              label={t('form.campus.label')}
              options={campusOptions}
              control={control}
              rules={{ validate: validateTextNotEmpty }}
            />
          </Col>

          {/* Código */}
          <Col xs={12} md={6} lg={3} className="mb-3">
            <TextInput
              name="code"
              label={t('form.code.label')}
              value={selectedPlanData?.code ?? '-'}
              disabled
            />
          </Col>

          {/* Modalidad */}
          <Col xs={12} md={6} lg={3} className="mb-3">
            <TextInput
              name="modality"
              label={t('form.modality.label')}
              value={selectedPlanData?.modality?.name ?? '-'}
              disabled
            />
          </Col>

          {/* Facultad */}
          <Col xs={12} md={6} lg={3} className="mb-3">
            <TextInput
              name="school"
              label={t('form.school.label')}
              value={selectedPlanData?.school?.name ?? '-'}
              disabled
            />
          </Col>

          {/* Cupos */}
          <Col xs={12} md={6} lg={3} className="mb-3">
            <TextInput
              name="quota"
              label={t('form.quota.label')}
              control={control}
              formatter={(value) => value.replace(/\D/g, '')}
            />
          </Col>

          {/* Plazo en días (solo para modalidad online-autoatendida) */}
          {isOnlineAutoAttended && (
            <Col xs={12} md={6} lg={3} className="mb-3">
              <TextInput
                name="completionDaysLimit"
                label={t('form.completionDaysLimit.label')}
                control={control}
                formatter={(value) => value.replace(/\D/g, '')}
                rules={{
                  required: msgValidations.required,
                  min: {
                    value: 1,
                    message: t('form.completionDaysLimit.minError'),
                  },
                }}
              />
            </Col>
          )}
        </Row>

        <Row>
          <Col xs={12} md={6} lg={4} className="offset-md-6 offset-lg-8">
            <Row>
              <Col>
                <Button
                  text={t('form.cancel')}
                  outlined
                  fullwidth
                  onClick={() =>
                    history.push(
                      `${PathsLayouts.continuousEducation}/academic-offers`,
                    )
                  }
                />
              </Col>
              <Col>
                <Button
                  text={t('wizard.next')}
                  type="submit"
                  loading={isSubmitting}
                  fullwidth
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </form>
    </Card>
  );
}
